import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { GeneratedDocumentTypeSchema } from '../deal/deal-documents.schemas';

const SigningRequestEventBase = z.object({
  signingRequestId: z.uuid(),
  generatedDocumentId: z.uuid().nullable(),
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  documentType: GeneratedDocumentTypeSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export const SigningRequestSentPayloadSchema = SigningRequestEventBase.extend({
  signerContactIds: z.array(z.uuid()).min(1), // request order; names are resolved by the reader (ADR 0015 §PII)
});
export type SigningRequestSentPayload = z.infer<typeof SigningRequestSentPayloadSchema>;

export const SigningRequestSignerSignedPayloadSchema = SigningRequestEventBase.extend({
  signerId: z.uuid(),
  signerContactId: z.uuid(),
  signedAt: z.iso.datetime(),
  signedCount: z.number().int().min(1),
  signerCount: z.number().int().min(1),
  sentByMemberId: z.uuid(),
  trigger: z.literal('SIGNER_PAGE'),
});
export type SigningRequestSignerSignedPayload = z.infer<typeof SigningRequestSignerSignedPayloadSchema>;

export const SigningRequestCompletedPayloadSchema = SigningRequestEventBase.extend({
  completedAt: z.iso.datetime(),
  sentByMemberId: z.uuid(),
});
export type SigningRequestCompletedPayload = z.infer<typeof SigningRequestCompletedPayloadSchema>;

export const SigningRequestSignedFileReadyPayloadSchema = SigningRequestEventBase.extend({
  sentByMemberId: z.uuid(),
  signedMediaFileId: z.uuid(),
  propertyDocumentId: z.uuid().nullable(), // null when the Objekt's filing found no source file
});
export type SigningRequestSignedFileReadyPayload = z.infer<typeof SigningRequestSignedFileReadyPayloadSchema>;

export const SigningRequestReminderDuePayloadSchema = SigningRequestEventBase.extend({
  // 1 for the first attempt; a TRANSIENT provider refusal re-publishes the event with the next number.
  attempt: z.number().int().min(1).default(1),
});
export type SigningRequestReminderDuePayload = z.infer<typeof SigningRequestReminderDuePayloadSchema>;

export const SigningAcceptanceReplyMarkedPayloadSchema = SigningRequestEventBase.extend({
  signerId: z.uuid(),
  signerContactId: z.uuid(),
  markedAt: z.iso.datetime(),
});
export type SigningAcceptanceReplyMarkedPayload = z.infer<typeof SigningAcceptanceReplyMarkedPayloadSchema>;

export const SigningRevocationReceivedPayloadSchema = SigningRequestEventBase.extend({
  signerId: z.uuid(),
  signerContactId: z.uuid(),
  receivedAt: z.iso.datetime(),
  sentByMemberId: z.uuid(),
  trigger: z.literal('SIGNER_PAGE'),
});
export type SigningRevocationReceivedPayload = z.infer<typeof SigningRevocationReceivedPayloadSchema>;

export const SigningAcceptanceReminderDuePayloadSchema = z.object({
  signingRequestId: z.uuid(),
  signerId: z.uuid(),
  // 1 for the first attempt; a TRANSIENT provider refusal re-publishes with the next number.
  attempt: z.number().int().min(1).default(1),
});
export type SigningAcceptanceReminderDuePayload = z.infer<typeof SigningAcceptanceReminderDuePayloadSchema>;

export const SigningRequestRemindedPayloadSchema = SigningRequestEventBase.extend({
  remindedSignerIds: z.array(z.uuid()).min(1),
});
export type SigningRequestRemindedPayload = z.infer<typeof SigningRequestRemindedPayloadSchema>;

export const SigningRequestWithdrawnPayloadSchema = SigningRequestEventBase.extend({
  signedCount: z.number().int().min(0), // signatures given before the withdrawal; they stay in the proof
});
export type SigningRequestWithdrawnPayload = z.infer<typeof SigningRequestWithdrawnPayloadSchema>;

export const SigningEmailDeliveryCheckDuePayloadSchema = z.object({
  emailId: z.uuid(),
  signingRequestId: z.uuid(),
  attempt: z.number().int().min(1),
  sentAt: z.iso.datetime(),
});
export type SigningEmailDeliveryCheckDuePayload = z.infer<typeof SigningEmailDeliveryCheckDuePayloadSchema>;

export const SigningEmailRetryDuePayloadSchema = z.object({
  emailId: z.uuid(), // the FAILED / TRANSIENT row to send again
  attempt: z.number().int().min(1), // the number of the attempt that failed; the original send is 1
});
export type SigningEmailRetryDuePayload = z.infer<typeof SigningEmailRetryDuePayloadSchema>;

export const SigningProofPrunedPayloadSchema = z.object({
  signingRequestId: z.uuid(),
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  prunedAt: z.iso.datetime(),
  actor: ActorSchema, // always the SYSTEM actor; the audit envelope reads it
});
export type SigningProofPrunedPayload = z.infer<typeof SigningProofPrunedPayloadSchema>;
